/**
 * Batch 18-D20 — Contextual Fitment Score Helper.
 *
 * Pure helper that scores a training artifact's vehicle fitment against
 * a ticket's YMME (year / make / model / engine) and returns both the
 * numeric boost and the winning match-tier string. Used by
 * `contextualTrainingScorer.scoreArtifactsForTicket` as the seventh
 * scoring stage; the result drives THREE downstream behaviors at the
 * scorer's call site:
 *
 *   1. Additive score boost (up to 60 points).
 *   2. Source flip — when matchType === 'exact_ymm', the scorer sets
 *      `source = 'vehicle_specific'`.
 *   3. Explanation prefix — exact_ymm prepends
 *      `"${year} ${make} ${model} specific • "` to the explanation;
 *      make_model prepends `"${make} ${model} relevant • "`. (The
 *      prefix logic itself lives at the scorer's call site, not in
 *      this helper.)
 *
 * `matchType` is therefore LOAD-BEARING — unlike the 18-D17/D18
 * adjacency helper's `matchedJobId` which was vestigial. The 18-D20
 * extraction preserves the field unchanged. No cleanup arc is
 * scheduled for a follow-up batch.
 *
 * Behavior is preserved BIT-FOR-BIT from
 * `src/utils/contextualTrainingScorer.ts` prior to Batch 18-D20:
 *
 *   - Empty `fitments` → `{ score: 0, matchType: null }`.
 *   - Missing `context.vehicleMake` → same.
 *   - Tier match in priority order — exact YMM → make+model →
 *     make-only.
 *   - Tier weights (`FITMENT_WEIGHTS` below, moved verbatim):
 *       EXACT_YMM_MATCH = 40 (override via `fitment.boost`)
 *       MAKE_MODEL_MATCH = 25
 *       MAKE_ONLY_MATCH = 10
 *       ENGINE_BONUS = +10 (additive, only when a tier already scored)
 *       MAX_FITMENT_BOOST = 60 (final cap)
 *   - Engine match uses CASE-INSENSITIVE `.includes()` substring
 *     (e.g. `"3.5L V6"`.toLowerCase().includes(`"3.5l"`) → true).
 *   - Make / model matching is case-insensitive equality.
 *   - Multiple fitment rows → best score wins; strict `>` means the
 *     FIRST equal-score row wins ties.
 *   - Final score is `Math.min(bestScore, MAX_FITMENT_BOOST)`.
 *
 * Intentional `||` semantics for `fitment.boost`:
 *
 *   Exact YMM uses `fitment.boost || FITMENT_WEIGHTS.EXACT_YMM_MATCH`.
 *   Because `||` is falsy-coalescing, a fitment row with `boost: 0`
 *   yields `EXACT_YMM_MATCH (= 40)`, NOT 0. This is the OPPOSITE of
 *   `trainingRelevanceScorer.scoreFitment` which uses `f.boost ?? 8`
 *   (nullish-coalescing — a `boost: 0` would yield 0 there). The
 *   18-D20 behavior-pin tests assert the contextual `||` semantics
 *   explicitly so any future "make it consistent with relevance"
 *   change is loud.
 *
 * Intentional divergence from `trainingRelevanceScorer.ts`:
 *
 *   The relevance scorer ships its own `scoreFitment` with different
 *   weights (cap 10 vs 60; exact 8 vs 40; make+model 5 vs 25;
 *   make-only 2 vs 10; engine +2 vs +10), different return shape
 *   (number vs `{ score, matchType }`), and different nullish
 *   handling (`??` vs `||`). These differences are INTENTIONAL —
 *   contextual fitment drives the schedule-aware Passport surface
 *   where vehicle-specific is a primary signal; relevance fitment is
 *   one of seven phases on the ticket Training tab. Merging the two
 *   is a product decision and is OUT OF SCOPE for 18-D20.
 *
 * Cycle avoidance: both type imports below are TYPE-ONLY, so
 * TypeScript elides them at compile time and no runtime module cycle
 * is created. Same pattern as 18-D2 (caps.ts), 18-D8
 * (engineExplanation.ts), 18-D12 (contextualExplanation.ts), 18-D14
 * (confidenceBadge.ts), and 18-D17 (contextualAdjacencyBoost.ts).
 *
 * Batch 18-D24: the fitment input shape is now imported via the shared
 * scoring scaffold (the 18-D22 re-export `TrainingContextualFitmentInfo`)
 * rather than directly from the contextual scorer. The scaffold alias
 * is structurally identical to the canonical `FitmentInfo` interface
 * declared in `contextualTrainingScorer.ts` — it is a verbatim
 * `export type { FitmentInfo as TrainingContextualFitmentInfo }`.
 *
 * `TicketContext` continues to import directly from the contextual
 * scorer because the 18-D21 audit deliberately did NOT re-export it
 * from the scaffold: there are two `TicketContext` types (contextual
 * + relevance scorer) with disjoint field sets, and the scaffold
 * already hosts `TrainingTicketContextBase` as a wide superset for
 * shared use. A third ticket-context concept on the scaffold would
 * grow the surface without solving a current pain.
 */

import type { TicketContext } from '@/utils/contextualTrainingScorer';
import type { TrainingContextualFitmentInfo } from '@/utils/training/scoring/types';

/**
 * Fitment scoring weights
 */
const FITMENT_WEIGHTS = {
  EXACT_YMM_MATCH: 40,      // year + make + model all match
  MAKE_MODEL_MATCH: 25,     // make + model match (year mismatch/missing)
  MAKE_ONLY_MATCH: 10,      // only make matches
  ENGINE_BONUS: 10,         // engine also matches
  MAX_FITMENT_BOOST: 60,    // cap total fitment boost
};

/**
 * Calculate fitment score based on vehicle year/make/model match
 */
export function calculateContextualFitmentScore(
  fitments: TrainingContextualFitmentInfo[],
  context: TicketContext
): { score: number; matchType: string | null } {
  if (!fitments.length || !context.vehicleMake) {
    return { score: 0, matchType: null };
  }

  let bestScore = 0;
  let bestMatchType: string | null = null;

  for (const fitment of fitments) {
    let score = 0;
    let matchType = '';

    const makeMatch = fitment.make &&
      context.vehicleMake?.toLowerCase() === fitment.make.toLowerCase();
    const modelMatch = fitment.model &&
      context.vehicleModel?.toLowerCase() === fitment.model.toLowerCase();
    const yearMatch = fitment.year &&
      context.vehicleYear === fitment.year;
    const engineMatch = fitment.engine &&
      context.vehicleEngine?.toLowerCase().includes(fitment.engine.toLowerCase());

    // Exact Y/M/M match
    if (yearMatch && makeMatch && modelMatch) {
      score = fitment.boost || FITMENT_WEIGHTS.EXACT_YMM_MATCH;
      matchType = 'exact_ymm';
    }
    // Make + Model match (year mismatch or null)
    else if (makeMatch && modelMatch) {
      score = FITMENT_WEIGHTS.MAKE_MODEL_MATCH;
      matchType = 'make_model';
    }
    // Make-only match
    else if (makeMatch) {
      score = FITMENT_WEIGHTS.MAKE_ONLY_MATCH;
      matchType = 'make_only';
    }

    // Engine bonus (additive)
    if (engineMatch && score > 0) {
      score += FITMENT_WEIGHTS.ENGINE_BONUS;
    }

    if (score > bestScore) {
      bestScore = score;
      bestMatchType = matchType;
    }
  }

  return {
    score: Math.min(bestScore, FITMENT_WEIGHTS.MAX_FITMENT_BOOST),
    matchType: bestMatchType
  };
}
